import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  CreateInsuranceCommissionSettlementPayload,
  InsuranceApiService,
  InsuranceCommissionSettlementDetail,
  InsurancePolicyListItem,
  InsuranceProducerListItem
} from './insurance-api.service';

type SettlementTab = 'summary' | 'accruals';

@Component({
  standalone: true,
  selector: 'ain-insurance-commission-settlement-detail',
  imports: [
    AinAmountPipe,
    AinDatePipe,
    AinPageComponent,
    AinStateComponent,
    RouterLink,
    FormsModule,
    InputTextModule,
    CurrencySelectorComponent,
    InfoHintComponent,
    PartySelectorComponent,
    ButtonModule,
    TableModule,
    SelectModule
  ],
  template: `
    <ain-page [title]="pageTitle()" [description]="t('insurance.commissions.settlementDetail.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'insurance.commissions.settlements.help.detail.title'"
        [descriptionKey]="'insurance.commissions.settlements.help.detail.description'"
        [bulletKeys]="[
          'insurance.commissions.settlements.help.detail.bullets.lifecycle',
          'insurance.commissions.settlements.help.detail.bullets.tabs',
          'insurance.commissions.settlements.help.detail.bullets.create'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (permissionDenied()) {
        <ain-state type="error" [title]="t('insurance.states.noPermission')" [message]="t('insurance.states.noPermissionMessage')" />
      } @else if (isCreateMode()) {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/insurance/commission-settlements']">{{ t('catalog.action.backToList') }}</a>
          <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="createSettlement()"></button>
        </div>
        <section class="card">
          <div class="section-title">
            <h3>{{ t('insurance.commissions.settlementDetail.createTitle') }}</h3>
            <ain-info-hint
              [titleKey]="'insurance.commissions.settlements.help.create.title'"
              [descriptionKey]="'insurance.commissions.settlements.help.create.description'"
              [bulletKeys]="[
                'insurance.commissions.settlements.help.create.bullets.legalEntity',
                'insurance.commissions.settlements.help.create.bullets.producer',
                'insurance.commissions.settlements.help.create.bullets.period'
              ]" />
          </div>
          <form class="grid" (ngSubmit)="createSettlement()">
            <label>
              <span>{{ t('insurance.commissions.settlementDetail.fields.legalEntity') }}</span>
              <ain-party-selector [(ngModel)]="createForm.legalEntityId" name="legalEntityId" />
            </label>
            <label>
              <span>{{ t('insurance.commissions.settlementDetail.fields.producer') }}</span>
              <p-select
                [options]="producerOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="createForm.producerId"
                name="producerId"
                [filter]="true"
                appendTo="body" />
            </label>
            <label>
              <span>{{ t('insurance.commissions.settlementDetail.fields.period') }}</span>
              <input pInputText [(ngModel)]="createForm.period" name="period" [placeholder]="t('insurance.commissions.settlementDetail.periodPlaceholder')" />
            </label>
            <label>
              <span>{{ t('insurance.commissions.settlementDetail.fields.currencyCode') }}</span>
              <ain-currency-selector [(ngModel)]="createForm.currencyCode" name="currencyCode" />
            </label>
          </form>
        </section>
        @if (error()) { <p class="error">{{ error() }}</p> }
      } @else if (!settlement()) {
        <ain-state type="error" [title]="t('insurance.states.notFound')" [message]="t('insurance.states.notFoundMessage')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/insurance/commission-settlements']">{{ t('catalog.action.backToList') }}</a>
        </div>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('insurance.commissions.settlements.sections.info') }}</h3>
            <ain-info-hint
              [titleKey]="'insurance.commissions.settlements.help.summary.title'"
              [descriptionKey]="'insurance.commissions.settlements.help.summary.description'"
              [bulletKeys]="[
                'insurance.commissions.settlements.help.summary.bullets.status',
                'insurance.commissions.settlements.help.summary.bullets.payment'
              ]" />
          </div>
          <div class="summary">
            <span>{{ t('insurance.commissions.settlements.fields.number') }}: <strong>{{ settlement()!.settlementNumber }}</strong></span>
            <span>{{ t('columns.status') }}: <strong>{{ t(statusKey(settlement()!.status)) }}</strong></span>
            <span>{{ t('insurance.commissions.settlements.fields.producer') }}: <strong>{{ producerLabel(settlement()!.producerId) }}</strong></span>
            <span>{{ t('insurance.commissions.settlements.fields.totalAmount') }}: <strong>{{ settlement()!.totalAmount | ainAmount }} {{ settlement()!.currencyCode }}</strong></span>
          </div>
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('insurance.commissions.settlements.sections.lifecycle') }}</h3>
            <ain-info-hint
              [titleKey]="'insurance.commissions.settlements.help.lifecycle.title'"
              [descriptionKey]="'insurance.commissions.settlements.help.lifecycle.description'"
              [bulletKeys]="[
                'insurance.commissions.settlements.help.lifecycle.bullets.approve',
                'insurance.commissions.settlements.help.lifecycle.bullets.pay'
              ]" />
          </div>
          <div class="lifecycle-actions">
            <button pButton type="button" [label]="t('insurance.commissions.settlementDetail.actions.approve')" [disabled]="settlement()!.status !== 'Draft' || saving()" (click)="approve()"></button>
            <button pButton type="button" severity="danger" [label]="t('insurance.commissions.settlementDetail.actions.pay')" [disabled]="settlement()!.status !== 'Approved' || saving()" (click)="pay()"></button>
          </div>
        </section>

        <section class="card">
          <div class="tabs">
            <button type="button" [class.active]="activeTab() === 'summary'" (click)="activeTab.set('summary')">{{ t('insurance.commissions.settlementDetail.tabs.summary') }}</button>
            <button type="button" [class.active]="activeTab() === 'accruals'" (click)="activeTab.set('accruals')">{{ t('insurance.commissions.settlementDetail.tabs.accruals') }}</button>
          </div>

          @if (activeTab() === 'summary') {
            <div class="lines-header">
              <h4>{{ t('insurance.commissions.settlementDetail.tabs.summary') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.commissions.settlements.help.tabs.summary.title'"
                [descriptionKey]="'insurance.commissions.settlements.help.tabs.summary.description'"
                [bulletKeys]="['insurance.commissions.settlements.help.tabs.summary.bullets.paymentBatch']" />
            </div>
            <div class="summary-grid">
              <p><strong>{{ t('insurance.commissions.settlementDetail.fields.paymentBatch') }}:</strong> {{ settlement()!.paymentBatchId ? t('insurance.commissions.settlementDetail.paymentBatchLinked') : '-' }}</p>
              <p><strong>{{ t('insurance.commissions.settlementDetail.fields.approvedAt') }}:</strong> {{ settlement()!.approvedAtUtc ? (settlement()!.approvedAtUtc | ainDate) : '-' }}</p>
              <p><strong>{{ t('insurance.commissions.settlementDetail.fields.paidAt') }}:</strong> {{ settlement()!.paidAtUtc ? (settlement()!.paidAtUtc | ainDate) : '-' }}</p>
            </div>
          } @else {
            <div class="lines-header">
              <h4>{{ t('insurance.commissions.settlementDetail.tabs.accruals') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.commissions.settlements.help.tabs.accruals.title'"
                [descriptionKey]="'insurance.commissions.settlements.help.tabs.accruals.description'"
                [bulletKeys]="['insurance.commissions.settlements.help.tabs.accruals.bullets.lines']" />
            </div>
            @if (settlement()!.accruals.length === 0) {
              <p>{{ t('insurance.commissions.settlementDetail.states.noAccruals') }}</p>
            } @else {
              <p-table [value]="settlement()!.accruals" styleClass="lines-table">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('insurance.commissions.accruals.fields.period') }}</th>
                    <th>{{ t('insurance.commissions.accruals.fields.policy') }}</th>
                    <th>{{ t('insurance.commissions.accruals.fields.amount') }}</th>
                    <th>{{ t('columns.status') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.period }}</td>
                    <td>
                      <button type="button" class="link-btn" (click)="openPolicy(row.policyId)">{{ policyLabel(row.policyId) }}</button>
                    </td>
                    <td>{{ row.amount | ainAmount }} {{ settlement()!.currencyCode }}</td>
                    <td>{{ t(accrualStatusKey(row.status)) }}</td>
                  </tr>
                </ng-template>
              </p-table>
            }
          }
        </section>

        @if (error()) { <p class="error">{{ error() }}</p> }
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; margin-bottom: 10px; }
    .section-title { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
    .section-title h3 { margin: 0; }
    .summary { display: flex; gap: 16px; flex-wrap: wrap; font-size: 14px; }
    .lifecycle-actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .tabs { display: flex; flex-wrap: wrap; gap: 8px; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; margin-bottom: 10px; }
    .tabs button { border: 0; background: transparent; padding: 6px 10px; border-radius: 8px; cursor: pointer; color: #334155; }
    .tabs button.active { background: #dbeafe; color: #1e40af; font-weight: 600; }
    .lines-header { display: flex; justify-content: space-between; align-items: center; gap: 8px; margin-bottom: 10px; }
    .lines-header h4 { margin: 0; }
    .summary-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    label { display: grid; gap: 6px; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .error { color: #b91c1c; }
    @media (max-width: 760px) {
      .grid, .summary-grid { grid-template-columns: 1fr; }
    }
  `]
})
export class InsuranceCommissionSettlementDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly api = inject(InsuranceApiService);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly error = signal('');
  readonly permissionDenied = signal(false);
  readonly settlement = signal<InsuranceCommissionSettlementDetail | null>(null);
  readonly isCreateMode = signal(false);
  readonly activeTab = signal<SettlementTab>('summary');
  readonly producers = signal<InsuranceProducerListItem[]>([]);
  readonly policies = signal<InsurancePolicyListItem[]>([]);

  readonly pageTitle = computed(() =>
    this.isCreateMode()
      ? this.t('insurance.commissions.settlementDetail.createTitle')
      : this.settlement()?.settlementNumber ?? this.t('insurance.commissions.settlementDetail.title'));

  createForm: CreateInsuranceCommissionSettlementPayload = {
    legalEntityId: '',
    producerId: '',
    period: null,
    currencyCode: 'IRR'
  };

  ngOnInit(): void {
    this.api.listProducers().subscribe({
      next: rows => this.producers.set(rows),
      error: () => this.producers.set([])
    });
    this.api.listPolicies().subscribe({
      next: rows => this.policies.set(rows),
      error: () => this.policies.set([])
    });

    const settlementId = this.route.snapshot.paramMap.get('settlementId');
    const isNewRoute = this.route.snapshot.url.at(-1)?.path === 'new';
    if (isNewRoute) {
      this.isCreateMode.set(true);
      this.loading.set(false);
      return;
    }
    if (!settlementId) {
      this.loading.set(false);
      return;
    }

    this.api.getCommissionSettlement(settlementId).subscribe({
      next: item => {
        this.settlement.set(item);
        this.loading.set(false);
      },
      error: err => {
        this.permissionDenied.set(err?.status === 403);
        this.error.set(err?.status === 403 ? '' : this.t('insurance.states.loadError'));
        this.loading.set(false);
      }
    });
  }

  producerOptions(): Array<{ label: string; value: string }> {
    return this.producers().map(item => ({ value: item.id, label: `${item.code} - ${item.name}` }));
  }

  producerLabel(producerId: string): string {
    const producer = this.producers().find(item => item.id === producerId);
    return producer ? `${producer.code} - ${producer.name}` : this.t('insurance.commissions.labels.unknownProducer');
  }

  policyLabel(policyId: string): string {
    const policy = this.policies().find(item => item.id === policyId);
    return policy?.policyNumber ?? this.t('insurance.commissions.labels.unknownPolicy');
  }

  openPolicy(policyId: string): void {
    void this.router.navigate(['/insurance/policies', policyId]);
  }

  createSettlement(): void {
    this.saving.set(true);
    this.error.set('');
    this.api.createCommissionSettlement(this.createForm).subscribe({
      next: item => {
        this.saving.set(false);
        this.settlement.set(item);
        this.isCreateMode.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  approve(): void {
    const current = this.settlement();
    if (!current || !confirm(this.t('insurance.commissions.settlementDetail.messages.approveConfirm'))) {
      return;
    }
    this.saving.set(true);
    this.api.approveCommissionSettlement(current.id).subscribe({
      next: item => {
        this.settlement.set(item);
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  pay(): void {
    const current = this.settlement();
    if (!current || !confirm(this.t('insurance.commissions.settlementDetail.messages.payConfirm'))) {
      return;
    }
    this.saving.set(true);
    this.api.payCommissionSettlement(current.id, { paymentBatchId: null }).subscribe({
      next: item => {
        this.settlement.set(item);
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  statusKey(status: InsuranceCommissionSettlementDetail['status']): string {
    return `insurance.commissions.settlements.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
  }

  accrualStatusKey(status: string): string {
    return `insurance.commissions.accruals.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
